/**
 * 이미지 리사이징/압축 유틸리티
 * - 업로드 전: Canvas 클라이언트 압축
 * - 표시 시: /api/file/view/{seq}?w= 서버 썸네일 (FileController thumb-cache)
 */

/** 목록·아바타·카드용 권장 폭 */
export const THUMB_W = {
  avatar: 160,
  card: 360,
  list: 160,
  detail: 800,
} as const

/**
 * 서버 온더플라이 썸네일 URL.
 * - number / `${seq}` → `/api/file/view/{seq}?w=`
 * - `/api/file/view/{seq}` · `/api/file/{seq}` → `?w=` 부착
 * - `/upload/...` 정적 원본은 `?w=` 불가 → 그대로(백엔드에서 view URL로 전환 권장)
 * - 이미 `?w=` 있으면 교체
 *
 * ⛔ 2026-09-22 — 기존 쿼리의 `k`(사적 첨부 열람권 서명)는 **반드시 싣고 간다**.
 *   종전에는 쿼리를 통째로 버리고 `?w=` 만 새로 붙였다. 서버가 토큰을 붙여 내려줘도
 *   이 함수를 거치면서 사라져, 토큰이 필요한 유형의 이미지가 전부 403 이 된다.
 *   ⚠️ 토큰이 필요한 유형은 «seq 가 아니라 서버가 준 URL» 을 넘겨야 한다.
 *      seq 만으로는 토큰을 만들 수 없다(서명 키는 서버에만 있다).
 */
export function thumbUrl(
  src: string | number | null | undefined,
  width: number = THUMB_W.card,
): string {
  if (src === null || src === undefined || src === '') return ''
  const w = Math.max(40, Math.min(1600, Math.round(width)))

  if (typeof src === 'number' && Number.isFinite(src)) {
    return `/api/file/view/${src}?w=${w}`
  }

  const raw = String(src).trim()
  if (!raw) return ''

  // 순수 숫자 문자열
  if (/^\d+$/.test(raw)) {
    return `/api/file/view/${raw}?w=${w}`
  }

  // /api/file/view/123 또는 /api/file/123 (+ optional query)
  const apiMatch = raw.match(/^(https?:\/\/[^/]+)?(\/api\/file(?:\/view)?\/(\d+))(?:\?(.*))?$/i)
  if (apiMatch) {
    const origin = apiMatch[1] || ''
    const seq = apiMatch[3]
    // 열람권 서명은 유지한다 — 버리면 사적 첨부가 403 이 된다
    const k = new URLSearchParams(apiMatch[4] || '').get('k')
    const keep = k ? `&k=${encodeURIComponent(k)}` : ''
    return `${origin}/api/file/view/${seq}?w=${w}${keep}`
  }

  // /upload/... 등 — 서버 리사이즈 불가
  return raw
}

/**
 * fileInfoSeq 우선, 없으면 URL에 thumbUrl 적용
 */
export function thumbFromSeqOrUrl(
  seq: number | null | undefined,
  url: string | null | undefined,
  width: number = THUMB_W.card,
): string {
  /* ⛔ 2026-09-22 — URL 에 열람권 서명(k)이 있으면 **URL 이 우선**이다.
     seq 로 다시 만들면 토큰이 없는 URL 이 되어 사적 첨부가 403 이 된다. */
  if (typeof url === 'string' && url.includes('k=')) return thumbUrl(url, width)
  if (seq != null && Number(seq) > 0) return thumbUrl(Number(seq), width)
  return thumbUrl(url, width)
}

/** <img> 로 그릴 수 있는 첨부 확장자 — 나머지(PDF·docx·hwp …)는 다운로드 링크로 보여 준다 */
const INLINE_IMAGE_EXTS = ['jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'bmp', 'heic']

/**
 * 첨부를 이미지로 그릴 것인가.
 * 서버 FILE_EXT 는 `.jpg` 처럼 점이 붙어 오고 목록 API 에 따라 빠지기도 해서 원본 파일명으로도 본다.
 * ⛔ 2026-09-30 §7-H26 — 게시글 상세가 첨부를 전부 <img> 로 그려 문서 첨부가 깨진 이미지로 보였고 받을 방법이 없었다.
 */
export function isImageAttachment(f: { fileExt?: string | null; fileOriNm?: string | null; fileNm?: string | null }): boolean {
  const fromExt = (f.fileExt || '').replace(/^\./, '').toLowerCase()
  const fromName = ((f.fileOriNm || f.fileNm || '').match(/\.([A-Za-z0-9]+)$/)?.[1] || '').toLowerCase()
  return INLINE_IMAGE_EXTS.includes(fromExt || fromName)
}

/**
 * 이미지 리사이징/압축 유틸리티
 * Canvas API 기반 클라이언트 측 이미지 최적화
 */

export interface ResizeOptions {
  /** 최대 너비 (기본: 1200px) */
  maxWidth?: number
  /** 최대 높이 (기본: 1200px) */
  maxHeight?: number
  /** JPEG 압축 품질 0.0~1.0 (기본: 0.8) */
  quality?: number
  /** 출력 MIME 타입 (기본: image/jpeg) */
  mimeType?: string
}

const DEFAULT_OPTIONS: Required<ResizeOptions> = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.8,
  mimeType: 'image/jpeg',
}

/**
 * 이미지 파일을 리사이징+압축하여 새 File 객체로 반환
 * - 이미지가 아닌 파일은 원본 그대로 반환
 * - maxWidth/maxHeight 이내면 압축만 수행 (리사이징 안함)
 */
export async function resizeImageFile(
  file: File,
  options?: ResizeOptions,
): Promise<File> {
  // 이미지 파일이 아니면 원본 반환
  if (!file.type.startsWith('image/')) {
    return file
  }

  const opts = { ...DEFAULT_OPTIONS, ...options }

  try {
    const blob = await resizeImageBlob(file, opts)
    // 확장자를 jpg로 변경 (JPEG 압축이므로)
    const ext = opts.mimeType === 'image/png' ? '.png' : '.jpg'
    const baseName = file.name.replace(/\.[^.]+$/, '')
    return new File([blob], `${baseName}${ext}`, {
      type: opts.mimeType,
      lastModified: Date.now(),
    })
  } catch {
    // v12-ok: 리사이즈 실패 — 원본 파일로 그대로 업로드한다(업로드 자체의 실패는 호출부가 안내)
    // Canvas 실패 시 원본 반환
    console.warn('이미지 리사이징 실패, 원본 사용')
    return file
  }
}

/**
 * 이미지를 Canvas로 리사이징+압축하여 Blob 반환
 */
function resizeImageBlob(
  file: File,
  opts: Required<ResizeOptions>,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      let { width, height } = img

      // 비율 유지하며 최대 크기 이내로 축소
      if (width > opts.maxWidth || height > opts.maxHeight) {
        const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas 컨텍스트 생성 실패'))
        return
      }

      // 고품질 렌더링
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Blob 변환 실패'))
          }
        },
        opts.mimeType,
        opts.quality,
      )
    }
    img.onerror = () => reject(new Error('이미지 로드 실패'))
    img.src = URL.createObjectURL(file)
  })
}
